/**
 * 表格导出 PDF 工具
 * 说明：
 *   1. 表格按 A4 横向渲染，内容超过单页高度时自动分页，每页重复表头，不会丢内容；
 *   2. 列宽固定（按配置分配），长文本自动换行，避免导出的表格被内容撑得变形；
 *   3. 支持指定列按相邻相同值纵向合并，与页面表格的合并展示保持一致；
 *   4. 依赖 html2canvas 与 jsPDF，页面未引入时会从 CDN 动态加载。
 */

const CDN = {
  html2canvas: 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js',
  jspdf: 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js'
}

const STYLE_ID = 'vg-pdf-export-style'

// 导出表格的渲染宽度（CSS 像素），用于换算纸张尺寸
export const RENDER_WIDTH = 1120

function loadScript (src, check) {
  return new Promise((resolve, reject) => {
    if (check()) {
      resolve()
      return
    }
    let script = document.querySelector('script[src="' + src + '"]')
    if (script && script.getAttribute('data-load-failed') === '1') {
      reject(new Error('脚本加载失败：' + src))
      return
    }
    if (!script) {
      script = document.createElement('script')
      script.src = src
      script.async = true
      document.head.appendChild(script)
    }
    let settled = false
    const finish = err => {
      if (settled) return
      settled = true
      clearInterval(timer)
      if (err) reject(err)
      else resolve()
    }
    // 脚本早已加载过时不会再触发 load 事件，用全局变量轮询兜底
    const timer = setInterval(() => {
      if (check()) finish()
    }, 100)
    script.addEventListener('load', () => {
      if (check()) finish()
      else finish(new Error('脚本加载失败：' + src))
    })
    script.addEventListener('error', () => {
      script.setAttribute('data-load-failed', '1')
      finish(new Error('脚本加载失败，请检查网络：' + src))
    })
    setTimeout(() => finish(new Error('脚本加载超时，请检查网络：' + src)), 20000)
  })
}

// 加载 html2canvas 与 jsPDF（未加载时从 CDN 引入）
export async function ensurePdfLibs () {
  await loadScript(CDN.html2canvas, () => typeof window.html2canvas !== 'undefined')
  await loadScript(CDN.jspdf, () => !!(window.jspdf && window.jspdf.jsPDF))
}

function ensureStyle (opt) {
  let style = document.getElementById(STYLE_ID)
  if (!style) {
    style = document.createElement('style')
    style.id = STYLE_ID
    document.head.appendChild(style)
  }
  style.textContent = [
    '.vg-pdf-page{box-sizing:border-box;background:#fff;color:#303133;font-family:' + opt.fontFamily + ';}',
    '.vg-pdf-head{text-align:center;padding-bottom:10px;}',
    '.vg-pdf-title{font-size:20px;font-weight:bold;line-height:1.5;}',
    '.vg-pdf-info{display:flex;justify-content:space-between;font-size:12px;padding-top:5px;}',
    '.vg-pdf-head-cont{padding-bottom:5px;}',
    '.vg-pdf-head-cont .vg-pdf-title{font-size:14px;}',
    '.vg-pdf-table{width:100%;border-collapse:collapse;table-layout:fixed;}',
    '.vg-pdf-table th,.vg-pdf-table td{border:1px solid #8c8c8c;padding:5px 7px;font-size:12px;line-height:1.5;vertical-align:middle;text-align:center;word-break:break-word;overflow-wrap:break-word;}',
    '.vg-pdf-table th{background:#eef1f6;font-weight:bold;}',
    '.vg-pdf-table td.is-left{text-align:left;}',
    '.vg-pdf-table td.is-right{text-align:right;}',
    '.vg-pdf-table td.is-wrap{white-space:pre-wrap;}',
    '.vg-pdf-table tr.is-total td{font-weight:bold;background:#f7f9fc;}',
    '.vg-pdf-foot{display:flex;justify-content:space-between;font-size:12px;color:#606266;padding-top:6px;}'
  ].join('')
}

// 分组高度超过一整页时允许的溢出比例：轻微超出时仍整组放在一页，由该页整体等比缩小
const ATOMIC_TOLERANCE = 1.25

function cellText (row, key) {
  const value = row[key]
  return value === null || value === undefined ? '' : String(value)
}

// 计算需要合并的列（mergeKey）在相邻行中的跨度，0 表示该单元格被上一行合并
function computeRowSpans (opt, rows) {
  const spans = rows.map(() => 1)
  if (!opt.mergeKey) return spans
  let index = 0
  while (index < rows.length) {
    const count = groupEndIndex(opt, rows, index) - index
    for (let i = 0; i < count; i++) {
      spans[index + i] = i === 0 ? count : 0
    }
    index += count
  }
  return spans
}

// 同一分组（mergeKey 列相邻且取值相同的行）的结束下标，合计行单独成组
function groupEndIndex (opt, rows, start) {
  const value = rows[start]._total || !opt.mergeKey ? '' : cellText(rows[start], opt.mergeKey)
  let end = start + 1
  if (!value) return end
  while (end < rows.length && !rows[end]._total && cellText(rows[end], opt.mergeKey) === value) {
    end++
  }
  return end
}

function computeColumnWidths (opt) {
  const fixedWidth = opt.columns.reduce((sum, col) => sum + (col.width || 0), 0)
  const flexColumns = opt.columns.filter(col => !col.width)
  const flexWeight = flexColumns.reduce((sum, col) => sum + (col.flex || 1), 0)
  const pool = Math.max(opt.renderWidth - fixedWidth, 0)
  return opt.columns.map(col => {
    if (col.width) return col.width
    return Math.max(col.minWidth || 80, Math.floor(pool * (col.flex || 1) / flexWeight))
  })
}

function createHead (opt, isFirst) {
  const head = document.createElement('div')
  head.className = 'vg-pdf-head' + (isFirst ? '' : ' vg-pdf-head-cont')
  const titleText = isFirst ? opt.title : (opt.titleContinuation || opt.title)
  if (titleText) {
    const title = document.createElement('div')
    title.className = 'vg-pdf-title'
    title.textContent = titleText
    head.appendChild(title)
  }
  if (isFirst && (opt.infoLeft || opt.infoRight)) {
    const info = document.createElement('div')
    info.className = 'vg-pdf-info'
    const left = document.createElement('span')
    left.textContent = opt.infoLeft || ''
    const right = document.createElement('span')
    right.textContent = opt.infoRight || ''
    info.appendChild(left)
    info.appendChild(right)
    head.appendChild(info)
  }
  return head
}

function createTable (opt, rows) {
  const table = document.createElement('table')
  table.className = 'vg-pdf-table'

  const colgroup = document.createElement('colgroup')
  computeColumnWidths(opt).forEach(width => {
    const col = document.createElement('col')
    col.style.width = width + 'px'
    colgroup.appendChild(col)
  })
  table.appendChild(colgroup)

  const thead = document.createElement('thead')
  const headRow = document.createElement('tr')
  opt.columns.forEach(col => {
    const th = document.createElement('th')
    th.textContent = col.title || ''
    headRow.appendChild(th)
  })
  thead.appendChild(headRow)
  table.appendChild(thead)

  const tbody = document.createElement('tbody')
  const spans = computeRowSpans(opt, rows)
  rows.forEach((row, index) => {
    const tr = document.createElement('tr')
    if (row._total) tr.className = 'is-total'
    opt.columns.forEach(col => {
      // 被合并掉的单元格不再渲染
      if (col.key === opt.mergeKey && spans[index] === 0) return
      const td = document.createElement('td')
      const classes = []
      if (col.align === 'left') classes.push('is-left')
      if (col.align === 'right') classes.push('is-right')
      if (col.wrap) classes.push('is-wrap')
      if (classes.length) td.className = classes.join(' ')
      if (col.key === opt.mergeKey && spans[index] > 1) td.rowSpan = spans[index]
      td.textContent = cellText(row, col.key)
      tr.appendChild(td)
    })
    tbody.appendChild(tr)
  })
  table.appendChild(tbody)
  return table
}

function createPage (opt, rows, pageIndex, pageTotal, isFirst) {
  const page = document.createElement('div')
  page.className = 'vg-pdf-page'
  page.style.width = opt.renderWidth + 'px'
  page.appendChild(createHead(opt, isFirst))
  page.appendChild(createTable(opt, rows))
  const foot = document.createElement('div')
  foot.className = 'vg-pdf-foot'
  const left = document.createElement('span')
  left.textContent = opt.footerLeft || ''
  const right = document.createElement('span')
  right.textContent = '第 ' + pageIndex + ' 页 / 共 ' + pageTotal + ' 页'
  foot.appendChild(left)
  foot.appendChild(right)
  page.appendChild(foot)
  return page
}

// 离屏挂载：不影响页面显示，同时保证能正常测量与截图
function mountOffscreen (node) {
  const holder = document.createElement('div')
  holder.style.position = 'absolute'
  holder.style.top = '0'
  holder.style.left = '-10000px'
  holder.style.zIndex = '-1'
  holder.appendChild(node)
  document.body.appendChild(holder)
  return holder
}

function heightOf (node) {
  return node ? node.offsetHeight : 0
}

/**
 * 导出表格 PDF
 * @param {Object}   options
 * @param {String}   options.fileName             文件名
 * @param {String}   options.title                首页标题
 * @param {String}   options.titleContinuation    续页标题
 * @param {String}   options.infoLeft / infoRight 首页副标题（左右两侧）
 * @param {String}   options.footerLeft           页脚左侧文字
 * @param {Array}    options.columns              列 [{key, title, width, flex, align, wrap, minWidth}]
 * @param {Array}    options.rows                 行数据，行内 _total 为 true 时作为合计行加粗
 * @param {String}   options.mergeKey             需要纵向合并的列 key（相邻相同值合并，与页面表格一致）
 */
export async function exportTablePdf (options) {
  const opt = Object.assign({
    fileName: 'export.pdf',
    title: '',
    titleContinuation: '',
    infoLeft: '',
    infoRight: '',
    footerLeft: '',
    columns: [],
    rows: [],
    mergeKey: '',
    renderWidth: RENDER_WIDTH,
    pageWidth: 297,
    pageHeight: 210,
    margin: 10,
    scale: 2,
    fontFamily: '"Microsoft YaHei","PingFang SC","Hiragino Sans GB","Heiti SC",Arial,sans-serif'
  }, options || {})

  if (!opt.columns.length) throw new Error('缺少表格列配置')

  await ensurePdfLibs()
  ensureStyle(opt)

  const contentWidth = opt.pageWidth - opt.margin * 2
  const contentHeight = opt.pageHeight - opt.margin * 2
  const pxPerMm = opt.renderWidth / contentWidth
  const pageCapacity = Math.floor(contentHeight * pxPerMm)

  // 1. 先整体渲染一次，测量标题区、表头与每一行的高度
  const measurePage = createPage(opt, opt.rows, 1, 1, true)
  const holder = mountOffscreen(measurePage)
  const headHeight = heightOf(measurePage.querySelector('.vg-pdf-head'))
  const footHeight = heightOf(measurePage.querySelector('.vg-pdf-foot'))
  const tableHeadHeight = heightOf(measurePage.querySelector('thead tr'))
  const rowHeights = Array.prototype.map.call(measurePage.querySelectorAll('tbody tr'), tr => tr.offsetHeight)
  // 续页标题区高度：临时插入测量
  const contHead = createHead(opt, false)
  measurePage.insertBefore(contHead, measurePage.firstChild)
  const contHeadHeight = heightOf(contHead)
  measurePage.removeChild(contHead)
  document.body.removeChild(holder)

  const firstPageLimit = pageCapacity - headHeight - tableHeadHeight - footHeight
  const nextPageLimit = pageCapacity - contHeadHeight - tableHeadHeight - footHeight

  // 2. 分页：同一个分组（一级项目）的行尽量放在同一页，避免同一评审项被拆到两页；
  //    分组本身高过一整页时，退化为按行分页
  const pages = []
  let current = {rows: [], height: 0}
  let index = 0
  while (index < opt.rows.length) {
    const groupEnd = groupEndIndex(opt, opt.rows, index)
    let groupHeight = 0
    for (let i = index; i < groupEnd; i++) {
      groupHeight += (rowHeights[i] || 24) + 2
    }
    // 分组能在一整页内放下（含少量溢出）时才整组处理
    const atomic = groupHeight <= nextPageLimit * ATOMIC_TOLERANCE
    if (atomic && current.rows.length) {
      const limit = pages.length === 0 ? firstPageLimit : nextPageLimit
      if (current.height + groupHeight > limit) {
        pages.push(current)
        current = {rows: [], height: 0}
      }
    }
    for (let i = index; i < groupEnd; i++) {
      const rowHeight = (rowHeights[i] || 24) + 2
      if (!atomic) {
        const limit = pages.length === 0 ? firstPageLimit : nextPageLimit
        if (current.rows.length && current.height + rowHeight > limit) {
          pages.push(current)
          current = {rows: [], height: 0}
        }
      }
      current.rows.push(opt.rows[i])
      current.height += rowHeight
    }
    index = groupEnd
  }
  pages.push(current)

  // 3. 逐页渲染写入 PDF
  const jsPDF = window.jspdf.jsPDF
  const pdf = new jsPDF('l', 'mm', 'a4')
  for (let index = 0; index < pages.length; index++) {
    const page = createPage(opt, pages[index].rows, index + 1, pages.length, index === 0)
    const pageHolder = mountOffscreen(page)
    let canvas
    try {
      canvas = await window.html2canvas(page, {
        scale: opt.scale,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false
      })
    } finally {
      document.body.removeChild(pageHolder)
    }
    const imageWidth = contentWidth
    const imageHeight = canvas.height * contentWidth / canvas.width
    // 单页内容过高时按比例缩小，保证内容完整
    const fit = Math.min(1, contentHeight / imageHeight)
    if (index > 0) pdf.addPage()
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', opt.margin, opt.margin, imageWidth * fit, imageHeight * fit)
  }
  pdf.save(opt.fileName)
  return pdf
}

export default {ensurePdfLibs, exportTablePdf, RENDER_WIDTH}
